import { useStore } from '@tanstack/react-store'
import { IoDocument } from 'react-icons/io5'
import { useEffect, useLayoutEffect, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  defaultDocumentsFilter,
  documentsListStore,
  resetDocumentsList,
} from 'shared/store/documentsList'
import { getAllFilterParams } from 'shared/utils/getFilterParams'
import { getURLFromFilterParams } from 'shared/utils/getURLFromFilterParams'
import { getDateTimeReadFormat } from 'shared/utils/dateFormatting'
import {
  Container,
  SnippetsContainer,
  SnippetsContainerMain,
  SnippetsContainerRightAside,
} from 'uikit/layout'
import { Snippets, SnippetsHeading, SnippetsItem, SnippetsList } from 'uikit'
import { HeadingItem, ItemBadge, ItemIcon, ItemText } from 'uikit/Snippets'
import { styleTypeTitle } from '../Documents.constants'
import { DocumentFilter } from './DocumentFilter'
import { useFetchDocumentsList } from './hooks/useFetchDocumentsList'

export const DocumentsList = () => {
  const { params } = useParams()
  const history = useHistory()

  const urlParams = useMemo(
    () => ({ ...defaultDocumentsFilter, ...getAllFilterParams(params) }),
    [params]
  )

  useLayoutEffect(() => {
    documentsListStore.setState({ filter: urlParams })
  }, [urlParams])

  const { filter } = useStore(documentsListStore)

  const {
    list,
    hasMore,
    totalElements,
    fetchNextPage,
    isFetching: loading,
  } = useFetchDocumentsList({ urlParams })

  useEffect(() => {
    return () => resetDocumentsList()
  }, [])

  const onNextPage = () => {
    fetchNextPage()
  }

  const onFilterChange = (nextFilter) => {
    documentsListStore.setState({ filter: nextFilter })

    const urlFilterParams = getURLFromFilterParams(nextFilter)
    const newPath = `/documents${urlFilterParams}`

    if (history.location.pathname !== newPath) {
      history.push(newPath)
    }
  }

  const onResetFilters = () => {
    documentsListStore.setState({ filter: defaultDocumentsFilter })
    history.push('/documents')
  }

  return (
    <Container>
      <SnippetsContainer indentTop={70}>
        <SnippetsContainerMain>
          <Snippets
            columns="42px minmax(150px, 1fr) minmax(350px, 1fr) 170px 180px 180px  180px"
            itemWidth="1000px"
          >
            <SnippetsHeading>
              <HeadingItem />
              <HeadingItem>Тип</HeadingItem>
              <HeadingItem>Название</HeadingItem>
              <HeadingItem>Неавторизованная зона</HeadingItem>
              <HeadingItem>Версия</HeadingItem>
              <HeadingItem>Дата обновления</HeadingItem>
              <HeadingItem>Дата создания</HeadingItem>
            </SnippetsHeading>
            <SnippetsList data={list} hasMore={hasMore} isLoading={loading} onNext={onNextPage}>
              {(item) => (
                <SnippetsItem to={`/documents/edit/${item.id}`}>
                  <ItemIcon IconComponent={IoDocument} />
                  <ItemText>
                    <ItemBadge>{styleTypeTitle[item?.style]}</ItemBadge>
                  </ItemText>
                  <ItemText>
                    {item?.names.find((item) => item.localization === 'RU')?.text}
                  </ItemText>
                  <ItemText>{item?.publicAccess ? 'Да' : 'Нет'}</ItemText>
                  <ItemText>{item?.version}</ItemText>
                  <ItemText>{getDateTimeReadFormat(item?.updateDate)}</ItemText>
                  <ItemText>{getDateTimeReadFormat(item?.createDate)}</ItemText>
                </SnippetsItem>
              )}
            </SnippetsList>
          </Snippets>
        </SnippetsContainerMain>
        <SnippetsContainerRightAside>
          <DocumentFilter
            totalItems={totalElements}
            value={filter}
            onChange={onFilterChange}
            onReset={onResetFilters}
          />
        </SnippetsContainerRightAside>
      </SnippetsContainer>
    </Container>
  )
}
